import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { Menu, X, Phone, Mail } from 'lucide-react';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const languages = [
    { code: 'en', label: 'EN' },
    { code: 'ru', label: 'RU' },
    { code: 'ar', label: 'AR' }
  ];

  const navItems = [
    { path: '/vlagen-os', label: 'VLAGEN OS' },
    { path: '/solutions', label: language === 'ru' ? 'Решения' : language === 'ar' ? 'الحلول' : 'Solutions' },
    { path: '/architecture', label: language === 'ru' ? 'Архитектура' : language === 'ar' ? 'الهندسة المعمارية' : 'Architecture' },
    { path: '/partners', label: t.nav.partners },
    { path: '/company', label: t.nav.company },
    { path: '/contact', label: t.nav.contact }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0b]" data-testid="header">
      {/* Top bar with contacts and language - hidden on mobile */}
      <div className="hidden sm:block border-b border-[#1f1f23]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-10 text-sm">
            <div className="flex items-center gap-6">
              <a href="tel:+971582965700" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="phone-link">
                <Phone size={14} />
                <span>+971 58 296 5700</span>
              </a>
              <a href="mailto:info@vlagengroup.com" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="email-link">
                <Mail size={14} />
                <span>info@vlagengroup.com</span>
              </a>
            </div>
            
            {/* Language Switcher */}
            <div className="flex items-center gap-1 bg-[#1a1a1c] rounded-full px-1 py-1" data-testid="language-switcher">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  data-testid={`lang-${lang.code}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    language === lang.code
                      ? 'bg-[#2ec4aa] text-black'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="border-b border-[#1f1f23]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo">
            <div className="w-8 h-8 bg-[#2ec4aa] rounded-lg flex items-center justify-center">
              <span className="text-black font-bold text-sm">V</span>
            </div>
            <span className="text-white font-semibold text-lg tracking-tight">VLAGEN</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.slice(1)}`}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === item.path
                    ? 'text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* CTA Button - Filled style */}
          <div className="hidden lg:block">
            <Link
              to="/contact"
              data-testid="header-cta"
              className="inline-flex items-center px-5 py-2 bg-[#2ec4aa] text-black font-medium text-sm rounded-full hover:bg-[#3ed4ba] transition-all"
            >
              {t.nav.cta}
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-gray-400 hover:text-white"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        </div>
      </div>

      {/* Mobile Navigation - Full screen overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 top-16 bg-[#0a0a0b] z-40 overflow-y-auto"
          data-testid="mobile-nav"
        >
          <div className="px-4 py-4 space-y-2 pb-24">
            {/* Mobile Language Switcher */}
            <div className="flex items-center justify-center gap-1 bg-[#1a1a1c] rounded-full px-1 py-1 mb-4">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  data-testid={`mobile-lang-${lang.code}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    language === lang.code
                      ? 'bg-[#2ec4aa] text-black'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
            
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-3 px-4 rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? 'text-white bg-[#1a1a1c]'
                    : 'text-gray-400 hover:text-white hover:bg-[#1a1a1c]/50'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-4">
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full text-center px-5 py-3 bg-[#2ec4aa] text-black font-semibold rounded-full"
              >
                {t.nav.cta}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
